// Ship production and Habitat (04 §5.5) and incoming-fleet detections (04 §3.4, §7.4 FLEET_INCOMING).
import { z } from 'zod';
import {
  idSchema,
  isoDateTimeSchema,
  resourcesSchema,
  shipCountsSchema,
  shipTypeSchema,
} from './common.js';
import { resourceBlockSchema } from './economy.js';

/** Habitat points (03 §4.4): ships built here that still exist plus undelivered queued ships. */
export const habitatSchema = z.strictObject({
  used: z.number().int().nonnegative(),
  capacity: z.number().int().nonnegative(),
});
export type Habitat = z.infer<typeof habitatSchema>;

export const shipProductionItemSchema = z.strictObject({
  id: idSchema,
  shipType: shipTypeSchema,
  quantity: z.number().int().positive(),
  delivered: z.number().int().nonnegative(),
  /** FIFO order on the system's one production line (G-30). */
  position: z.number().int().positive(),
  unitDurationMs: z.number().int().nonnegative(),
  /** Null while the batch waits behind another one. */
  startedAt: isoDateTimeSchema.nullable(),
  nextShipAt: isoDateTimeSchema.nullable(),
  /** When the last ship of the batch is delivered. */
  completesAt: isoDateTimeSchema.nullable(),
});
export type ShipProductionItem = z.infer<typeof shipProductionItemSchema>;

export const shipProductionBlockerSchema = z.strictObject({
  code: z.literal('REQUIREMENTS_NOT_MET'),
  requirement: z.literal('space_dock'),
  requiredLevel: z.number().int().positive(),
});

/** One ship class the system could build now (slice classes only, S-2). Resources are never a blocker. */
export const shipOptionSchema = z.strictObject({
  cost: resourcesSchema,
  unitDurationMs: z.number().int().nonnegative(),
  habitatPoints: z.number().int().nonnegative(),
  speedLyph: z.number().positive(),
  cargo: z.number().int().nonnegative(),
  blocker: shipProductionBlockerSchema.nullable(),
});
export type ShipOption = z.infer<typeof shipOptionSchema>;

export const shipProductionRequestSchema = z.strictObject({
  shipType: shipTypeSchema,
  quantity: z.number().int().min(1).max(10_000),
  stealth: z.boolean().default(false),
});
export type ShipProductionRequest = z.input<typeof shipProductionRequestSchema>;

export const shipProductionResponseSchema = z.strictObject({
  item: shipProductionItemSchema,
  resources: resourceBlockSchema,
  habitat: habitatSchema,
});
export type ShipProductionResponse = z.infer<typeof shipProductionResponseSchema>;

export const cancelShipProductionResponseSchema = z.strictObject({
  cancelledId: idSchema,
  refunded: resourcesSchema,
  shipsDelivered: z.number().int().nonnegative(),
});
export type CancelShipProductionResponse = z.infer<typeof cancelShipProductionResponseSchema>;

export const DETAIL_LEVELS = ['time_only', 'size_class', 'approximate', 'exact'] as const;
export const detailLevelSchema = z.enum(DETAIL_LEVELS);

/**
 * An incoming hostile fleet as its target's owner sees it (G-33): arrival time, the Observatory
 * detail, the attacker and the exit system — never the origin or the mission.
 */
export const fleetDetectionSchema = z.strictObject({
  fleetId: idSchema,
  targetSystemId: idSchema,
  arrivesAt: isoDateTimeSchema,
  detailLevel: detailLevelSchema,
  sizeClass: z.enum(['small', 'medium', 'large', 'huge']).optional(),
  ships: shipCountsSchema.optional(),
  attacker: z.strictObject({
    playerId: idSchema,
    displayName: z.string(),
    allianceTag: z.string().nullable(),
  }),
  exitSystemId: idSchema,
});
export type FleetDetection = z.infer<typeof fleetDetectionSchema>;
